import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "card-form" }
const _hoisted_2 = { class: "card-header" }
const _hoisted_3 = { class: "h3 text-bold" }
const _hoisted_4 = { class: "card-content row-gap-s3" }
const _hoisted_5 = { class: "card-footer" }

import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ENABLE_CROSS_CHAIN } from '@/helpers/crossChainEnv.helper';
import { debounce } from '@/helpers/utils';
import { useEVMWallet } from '@/store/modules/wallet/useEVMWallet';
import { useSwap } from '@/store/modules/swap/useSwap';
import { Fields } from '@/store/modules/swap/swap.module';
import Spinner from '@/components/Spinner.vue';
import SwapSetting from '@/views/pages/swap/components/SwapSetting.vue';
import SwapInputPanel from '@/views/pages/swap/components/SwapInputPanel.vue';
import UpdateTimerIcon from '@/components/UpdateTimerIcon.vue';
import DestinationAddress from '@/components/DestinationAddress.vue';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { useAllSteps } from '@/composables/useAllSteps';
import WalletConnect from '@/views/layout/wallet/WalletConnect.vue';
import { PRICE_IMPACT_MAX } from '@/store/modules/swap/constants/SWAP_PRICE_IMPACT_MAX';
import { useSwapInfo } from '@/store/modules/swap/useSwapInfo';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import { checkIfNetworkIdDisabledToAdd } from '@/helpers/network-disable.helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'SwapForm',
  props: {
    swapInProgress: { type: Boolean }
  },
  emits: ["onSwap"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'onSwap'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  swapInProgress?: boolean;
}

const DEFAULT_CLICK_DEBOUNCE_DELAY = 300;

// TODO: Moved to translation
const SWAP = {
  enterDestinationAddress: 'Enter destination address',
};

const { t } = useI18n();
const { walletState } = useEVMWallet();
const {
  swapForm,
  setDestinationAddress,
  setAllowance,
  switchInputs,
  onFormChange,
  doUpdateRequestByTimer,
} = useSwap();
const { swapInfo } = useSwapInfo();

const { isInProgress } = useAllSteps();





const isBalanceError = ref(false);
const destinationAddress = ref(swapForm.output.destinationAddress ?? '');

const setBalanceError = event => (isBalanceError.value = event);
const isChangeDirectionDisabled = computed(() =>
  ENABLE_CROSS_CHAIN ? swapForm.input?.token?.chainId !== swapForm.output?.token?.chainId : false,
);

const needDestinationAddress = computed(() => {
  if (!swapForm.input?.token || !swapForm.output?.token) return false;

  return (
    ENABLE_FAKE_CARDANO_NETWORK && swapForm.input.token.chainId !== swapForm.output.token.chainId
  );
});
const noSwapInfo = computed(() => Object.keys(swapForm.bestTrade).length === 0);
const isPriceImpactTooBig = computed(() => Number(swapInfo.priceImpact) > PRICE_IMPACT_MAX);
const isNetworkDisabledToAdd = computed(() => checkIfNetworkIdDisabledToAdd(DEFAULT_NETWORK_ID));
const isSwapDisabled = computed(
  () =>
    isBalanceError.value ||
    !!swapForm.errors.length ||
    props.swapInProgress ||
    swapForm.formLoading ||
    noSwapInfo.value ||
    swapForm.input.amount === null ||
    swapForm.output.amount === null ||
    (needDestinationAddress.value && !swapForm.output.destinationAddress) ||
    (ENABLE_FAKE_CARDANO_NETWORK && isInProgress.state) ||
    isPriceImpactTooBig.value ||
    isNetworkDisabledToAdd.value,
);
const swapButtonLabel = computed(() => {
  if (swapForm.input.amount === null && swapForm.output.amount === null) {
    return t('swap.enterAmount');
  }

  if (needDestinationAddress.value && !swapForm.output.destinationAddress) {
    return SWAP.enterDestinationAddress;
  }

  return t('swap.swap');
});
const isAllowanceDisabled = computed(
  () =>
    isBalanceError.value ||
    !!swapForm.errors.length ||
    swapForm.allowanceRequestInProgress ||
    isPriceImpactTooBig.value,
);
const notAllowance = computed(() =>
  ENABLE_FAKE_CARDANO_NETWORK ? false : swapForm.input.token && !swapForm.hasAllowance,
);
const iconClass = computed(() => {
  let cls = 'card-form-icon icon-swap m-0';
  cls += isChangeDirectionDisabled.value ? ' icon-disabled' : ' clickable';
  return cls;
});

const isShowSwapInfo = computed(() => swapForm.isShowSwapInfo);
const isUpdateTimer = computed(() => isShowSwapInfo.value);

const onSwitchTokens = debounce(async () => {
  if (!isChangeDirectionDisabled.value) {
    console.log('on Switch Tokens');
    await switchInputs();
  }
}, DEFAULT_CLICK_DEBOUNCE_DELAY);

const onSwap = () => emits('onSwap');

const onUpdateClick = debounce(async () => {
  console.log('click for update');
  await onFormChange(true);
}, DEFAULT_CLICK_DEBOUNCE_DELAY);

const updateByTimer = async () => {
  console.log('update By timer');
  await doUpdateRequestByTimer();
};

const onSetAllowance = async () => {
  await setAllowance();
};

watch(
  () => walletState.isInjected,
  async isInjected => {
    if (isInjected) {
      await onFormChange();
    }
  },
);

watch([needDestinationAddress, destinationAddress], ([needAddress, address]) => {
  if (!ENABLE_FAKE_CARDANO_NETWORK) return;

  setDestinationAddress(needAddress && address ? address : null);
  if (!needAddress) {
    destinationAddress.value = '';
  }
});

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('swap.swap')), 1),
      (_unref(isUpdateTimer))
        ? (_openBlock(), _createBlock(UpdateTimerIcon, {
            key: 0,
            class: "ml-auto mr-s3",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(onUpdateClick)())),
            onTimerUpdate: _cache[1] || (_cache[1] = ($event: any) => (updateByTimer()))
          }))
        : _createCommentVNode("", true),
      _createVNode(SwapSetting)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", null, [
        _createVNode(SwapInputPanel, {
          field: _unref(Fields).InputA,
          onBalanceError: _cache[2] || (_cache[2] = ($event: any) => (setBalanceError($event)))
        }, null, 8, ["field"])
      ]),
      _createElementVNode("div", null, [
        _createVNode(SwapInputPanel, {
          field: _unref(Fields).InputB
        }, null, 8, ["field"])
      ]),
      _createElementVNode("div", {
        class: _normalizeClass(_unref(iconClass)),
        onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(onSwitchTokens) && _unref(onSwitchTokens)(...args)))
      }, null, 2)
    ]),
    (_unref(needDestinationAddress))
      ? (_openBlock(), _createBlock(DestinationAddress, {
          key: 0,
          modelValue: destinationAddress.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((destinationAddress).value = $event)),
          class: "mt-s3"
        }, null, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      (!_unref(walletState).isInjected)
        ? (_openBlock(), _createBlock(WalletConnect, {
            key: 0,
            ref: "wallet",
            "button-class": "w-full button-card-form"
          }, null, 512))
        : (_unref(notAllowance) && !_unref(noSwapInfo))
          ? (_openBlock(), _createBlock(_component_Button, {
              key: 1,
              class: "w-full button-card-form",
              disabled: _unref(isAllowanceDisabled),
              onClick: onSetAllowance
            }, {
              default: _withCtx(() => [
                (_unref(swapForm).allowanceRequestInProgress)
                  ? (_openBlock(), _createBlock(Spinner, {
                      key: 0,
                      size: "24",
                      class: "mr-s2"
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" " + _toDisplayString(_unref(t)('swap.approve', { token: _unref(swapForm).input.token?.symbol })), 1)
              ]),
              _: 1
            }, 8, ["disabled"]))
          : (_openBlock(), _createBlock(_component_Button, {
              key: 2,
              class: "w-full button-card-form",
              disabled: _unref(isSwapDisabled),
              onClick: onSwap
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(swapButtonLabel)), 1)
              ]),
              _: 1
            }, 8, ["disabled"]))
    ])
  ]))
}
}

})